/**
 * Manifest loader + minimal validator.
 *
 * The full JSON Schema lives in `schema.json` and is used by the
 * `tools/validate-manifest.mjs` CLI (which can pull in ajv). At runtime in
 * the browser we keep it dependency-free with a small structural check.
 */

const REQUIRED = ['id', 'name', 'version', 'entry', 'players'];

/** Throws if the manifest is missing required fields. */
export function validateManifest(manifest) {
  if (!manifest || typeof manifest !== 'object') {
    throw new Error('Manifest is not an object');
  }
  for (const key of REQUIRED) {
    if (!(key in manifest)) {
      throw new Error('Manifest missing required field: ' + key);
    }
  }
  if (!/^[a-z0-9-]{2,40}$/.test(manifest.id)) {
    throw new Error('Manifest id must match /^[a-z0-9-]{2,40}$/');
  }
  if (!manifest.players || typeof manifest.players.min !== 'number' || typeof manifest.players.max !== 'number') {
    throw new Error('Manifest.players must have numeric min and max');
  }
  if (manifest.players.min > manifest.players.max) {
    throw new Error('Manifest.players.min cannot exceed max');
  }
  return manifest;
}

/** Fill missing optional fields with sensible defaults. */
export function defaultManifest(manifest) {
  const m = Object.assign({}, manifest);
  m.description = m.description || '';
  m.author = m.author || 'unknown';
  m.license = m.license || 'MIT';
  m.category = m.category || 'misc';
  m.tags = m.tags || [];
  m.orientation = m.orientation || 'any';
  m.controls = m.controls || ['touch', 'keyboard'];
  m.languages = m.languages || ['en'];
  m.supportsBots = !!m.supportsBots;
  m.supportsSpectators = !!m.supportsSpectators;
  m.supportsReconnect = m.supportsReconnect !== false;
  m.network = Object.assign(
    { tickRateHz: 20, snapshotRateHz: 10, authoritative: 'host', simMode: 'synchronized' },
    m.network || {}
  );
  // Coerce an unknown simMode back to the default rather than crashing later.
  if (m.network.simMode !== 'synchronized' && m.network.simMode !== 'independent') {
    m.network.simMode = 'synchronized';
  }
  m.features = Object.assign({ leaderboard: false, achievements: false, replay: false }, m.features || {});
  m.requirements = Object.assign(
    {
      webgl: false,
      audio: true,
      camera: false,
      microphone: false,
      gyroscope: false,
      ar: 'none',
      tracking: []
    },
    m.requirements || {}
  );
  if (!Array.isArray(m.requirements.tracking)) m.requirements.tracking = [];
  return m;
}

/** Fetch + validate a manifest from a URL. */
export async function loadManifest(url) {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error('Failed to fetch manifest at ' + url + ': ' + res.status);
  const json = await res.json();
  return defaultManifest(validateManifest(json));
}
